'use client';

import { FC, useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import CategorySidebar from './CategorySidebar';
import CourseList from './CourseList';
import { tutorialService } from '@/services/tutorialService';
import { TutorialSection } from '@/types/tutorial';

const PlaybooksContainer: FC = () => {
    const router = useRouter();
    const [sections, setSections] = useState<TutorialSection[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>('');
    const [categories, setCategories] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const isScrollingRef = useRef(false);

    useEffect(() => {
        const fetchSections = async () => {
            try {
                const data = await tutorialService.getSections();
                
                // Extract unique categories
                const uniqueCategories = Array.from(new Set(data.map(s => s.category || 'Sales')));
                setCategories(uniqueCategories);

                // Set initial category if available
                if (uniqueCategories.length > 0) {
                    setActiveCategory(uniqueCategories[0]);
                }

                // Fetch lessons for each section in parallel
                const sectionsWithLessons = await Promise.all(
                    data.map(async (section) => {
                        try {
                            const sectionWithLessons = await tutorialService.getSectionById(section.id);
                            return sectionWithLessons || section;
                        } catch (error) {
                            console.error(`Failed to load lessons for section ${section.id}:`, error);
                            return section; // Return section without lessons if fetch fails
                        }
                    })
                );

                setSections(sectionsWithLessons);
            } catch (error) {
                console.error('Failed to load sections', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSections();
    }, []);

    // Track scroll position to update active category
    useEffect(() => {
        if (categories.length === 0 || isScrollingRef.current) return;

        const handleScroll = () => {
            if (isScrollingRef.current) return;

            const scrollPosition = window.scrollY + 150; // Offset for better UX

            // Find which category section is currently in view
            for (let i = categories.length - 1; i >= 0; i--) {
                const category = categories[i];
                const element = categoryRefs.current[category];
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const elementTop = rect.top + window.scrollY;
                    
                    if (scrollPosition >= elementTop) {
                        setActiveCategory(category);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [categories]);

    const handleStartCourse = (sectionId: string) => {
        // Navigate to detail page using Next.js router
        router.push(`/playbooks/${sectionId}`);
    };

    const handleCategoryClick = (category: string) => {
        setActiveCategory(category);
        isScrollingRef.current = true;
        const element = categoryRefs.current[category];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Reset scrolling flag after scroll completes
            setTimeout(() => {
                isScrollingRef.current = false;
            }, 1000);
        } else {
            isScrollingRef.current = false;
        }
    };

    const setCategoryRef = (category: string, element: HTMLDivElement | null) => {
        categoryRefs.current[category] = element;
    };

    if (loading) {
        return <div className="min-h-[400px] flex items-center justify-center">Loading...</div>;
    }

    return (
        <div className='max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12'>
            <div className='flex flex-col lg:flex-row'>
                {/* Left Sidebar */}
                <CategorySidebar
                    categories={categories}
                    activeCategory={activeCategory}
                    onSelectCategory={handleCategoryClick}
                />

                {/* Main Content Area */}
                <CourseList
                    sections={sections}
                    onStartCourse={handleStartCourse}
                    setCategoryRef={setCategoryRef}
                />
            </div>
        </div>
    );
};

export default PlaybooksContainer;
